import React, { useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, FileText, CalendarOff, ArrowDownRight,
  ArrowRightLeft, Info, ArrowUpDown
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import EtatVide from '../ui/EtatVide';
import { formatMontant } from './nosDepensesUtils';

/**
 * L'état de l'école pour un mois : ce qui est dû par classe, ce qui est rentré.
 *
 * Chaque gestionnaire ne voit que son portefeuille. Le validateur devait se
 * connecter sur chaque compte pour reconstituer l'état de l'école ; la caisse
 * centrale étant l'entonnoir, elle doit le lire d'un seul endroit — et au
 * niveau où se prend une décision, la CLASSE.
 *
 * DEUX AXES, TENUS SÉPARÉS
 * ------------------------
 * Le mois d'ÉCHÉANCE (pour quel mois le paiement compte) et le mois
 * d'ENREGISTREMENT (quand il est entré). Un règlement de janvier fait en
 * février apparaît dans le tableau de janvier comme recouvrement, et dans
 * celui de février comme rattrapage — jamais compté deux fois dans le même
 * total.
 */

/**
 * Le gabarit des colonnes, déclaré une fois : en-tête, lignes et total s'y
 * réfèrent — c'est ce qui garantit l'alignement.
 *
 * `gap-x-0` et des paddings par cellule plutôt qu'un écart de grille : les
 * séparateurs verticaux doivent toucher le bord de la ligne, un `gap` les
 * laisserait flotter au milieu du vide.
 */
/**
 * Le gabarit, et la repartition de la place.
 *
 * La colonne d'identite etait en `1.2fr` : elle absorbait toute la largeur
 * disponible et laissait un grand vide a gauche pendant que les chiffres se
 * serraient a droite. Elle est desormais FIXE, et c'est « Reglé un autre
 * mois » qui prend le mou — c'est la seule colonne qui a quelque chose a y
 * mettre : le detail des mois de reglement.
 */
const GRILLE = 'grid grid-cols-[224px_54px_repeat(3,132px)_minmax(240px,1fr)_132px_78px] items-center';

/**
 * Une grille complète sur 8 colonnes et 26 lignes ferait cage. Un séparateur
 * aux seules FRONTIÈRES DE SENS suffit à guider l'oeil, et dit en plus ce que
 * les colonnes ont en commun :
 *
 *   identité │ ce qui est dû et reçu │ comment c'est rentré │ ce qui reste
 */
const SEPARATEUR = 'border-l border-gray-200';

/**
 * Les indices de COLONNES qui ouvrent un groupe.
 *   0 « Attendu »           -> ce qui est dû et reçu
 *   2 « dont dans le mois » -> comment c'est rentré
 *   4 « Reste dû »          -> ce qui reste
 */
const GROUPES = new Set([0, 2, 4]);

/**
 * Les intitulés nomment l'AXE, sur deux lignes.
 *
 * « dont dans le mois » et « dont en rattrapage » ne disaient pas de quoi ils
 * parlaient : on pouvait lire « rattrapage » comme « frais d'un autre mois
 * tombés ici », ce qui est l'exact contraire. Toute ligne de ce tableau ne
 * concerne QUE les frais du mois choisi ; seule change la date à laquelle ils
 * ont été réglés.
 *
 * Les frais d'AUTRES mois encaissés pendant celui-ci vivent dans le second
 * bloc, « Encaissé pendant … » — deux axes, deux endroits, jamais le même mot.
 */
const COLONNES = (mois) => [
  {
    cle: 'attendu',
    libelle: `Frais de ${mois}`,
    precision: 'dus par la classe',
    aide: `Ce que la classe doit au titre de ${mois}`
  },
  {
    cle: 'encaisse',
    libelle: 'Réglé au total',
    precision: 'toutes dates confondues',
    aide: `Tout ce qui a été reçu pour ${mois}, quelle que soit la date de règlement`
  },
  {
    cle: 'pendant',
    libelle: `Réglé pendant ${mois}`,
    precision: 'payés à temps',
    aide: `Frais de ${mois} réglés dans le mois de ${mois}`
  },
  {
    cle: 'rattrapage',
    libelle: 'Réglé un autre mois',
    // « payés avant ou après » ne disait rien qu'on ne sache déjà. Ce qui
    // manque, c'est QUEL mois — et il varie d'une ligne à l'autre, donc il
    // s'affiche dans la cellule.
    precision: 'avec les mois de règlement',
    aide: `Frais de ${mois} réglés en dehors de ${mois}. Ce ne sont PAS les frais d'un autre mois.`
  },
  {
    cle: 'reste',
    libelle: 'Reste dû',
    precision: 'non encore réglé',
    aide: `Frais de ${mois} qui n'ont encore rien reçu`
  }
];

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** `2026-01` -> `janv. 2026`, ou `janv.` si l'année se devine. */
const nommerMois = (reference, avecAnnee = false) => {
  const [annee, mois] = String(reference || '').split('-').map(Number);
  const nom = MOIS_COURTS[(mois || 1) - 1] || reference;
  return avecAnnee ? `${nom} ${annee}` : nom;
};

const abrege = (montant) => {
  const valeur = Math.round(Number(montant) || 0);
  if (valeur >= 1000000) return `${(valeur / 1000000).toFixed(1).replace('.0', '')} M`;
  if (valeur >= 1000) return `${Math.round(valeur / 1000)} k`;
  return String(valeur);
};

/**
 * La cellule « Réglé un autre mois » : le montant, puis QUELS mois.
 *
 * Jusqu'à six mois par classe : les deux plus gros s'affichent, le reste tient
 * dans un compteur, et l'infobulle porte la liste entière. Tout afficher
 * rendrait les 26 lignes illisibles ; ne rien afficher était la remarque
 * initiale.
 */
const CelluleRattrapage = ({ valeur, mois = [], groupe = false }) => {
  const tries = [...mois].sort((a, b) => b.total - a.total);
  const tete = tries.slice(0, 2);
  const reste = tries.length - tete.length;
  const complet = tries
    .map((m) => `${nommerMois(m.mois, true)} : ${formatMontant(m.total)}`)
    .join(String.fromCharCode(10));

  return (
    <span className={`flex flex-col items-end px-2.5 py-2 ${groupe ? SEPARATEUR : ''}`} title={complet || undefined}>
      <span className={`text-right text-xs font-medium tabular-nums ${valeur > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
        {formatMontant(valeur || 0)}
      </span>
      {tete.length > 0 && (
        <span className="mt-0.5 text-right text-[10px] leading-tight text-gray-500">
          {tete.map((m) => `${nommerMois(m.mois)} ${abrege(m.total)}`).join(' · ')}
          {reste > 0 && <span className="text-gray-400"> · +{reste}</span>}
        </span>
      )}
    </span>
  );
};

const Cellule = ({ valeur, ton = 'text-gray-800', gras = false, groupe = false }) => (
  <span className={`px-2.5 py-2 text-right text-xs tabular-nums ${gras ? 'font-bold' : 'font-medium'} ${ton} ${groupe ? SEPARATEUR : ''}`}>
    {formatMontant(valeur || 0)}
  </span>
);

/** Le taux, en pastille : le rouge n'y sert qu'en dessous d'un tiers. */
const Taux = ({ valeur }) => {
  const ton = valeur >= 80 ? 'bg-emerald-100 text-emerald-800'
    : valeur >= 33 ? 'bg-amber-100 text-amber-800'
      : 'bg-red-100 text-red-800';
  return (
    <span className={`flex h-full items-center justify-end px-2.5 py-2 ${SEPARATEUR}`}>
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${ton}`}>
        {valeur} %
      </span>
    </span>
  );
};

const EtatEcoleSection = ({
  isExpanded, onToggleSection,
  moisBilan, setMoisBilan, moisDisponibles,
  bilanMensuel, chargement
}) => {
  const [tri, setTri] = useState('reste');

  const parClasse = bilanMensuel?.recouvrement_par_classe || {};
  const enregistrements = bilanMensuel?.enregistrements_du_mois || {};
  const periode = bilanMensuel?.periode || {};
  const nomMois = periode.nom || 'ce mois';

  const moisScolaires = moisDisponibles?.moisScolaires || [];
  const horsCalendrier = moisDisponibles?.horsCalendrier || [];

  const totaux = parClasse.totaux;
  const classes = useMemo(() => {
    const liste = [...(parClasse.classes || [])];
    if (tri === 'reste') return liste.sort((a, b) => b.reste - a.reste);
    if (tri === 'taux') return liste.sort((a, b) => a.taux - b.taux);
    return liste.sort((a, b) =>
      (a.section || '').localeCompare(b.section || '') || (a.classe || '').localeCompare(b.classe || ''));
  }, [parClasse.classes, tri]);

  return (
    <div className="px-6">
      <Card>
        <CardHeader className="cursor-pointer transition-colors hover:bg-gray-50" onClick={onToggleSection}>
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown size={24} className="text-gray-600" />
            ) : (
              <ChevronRight size={24} className="text-gray-600" />
            )}
            <div>
              <CardTitle className="flex items-center gap-2 text-xl text-[#246990]">
                <FileText size={24} />
                État de l'école — recouvrement par classe
              </CardTitle>
              <p className="mt-1 text-sm text-gray-500">
                Ce que chaque classe doit pour le mois, ce qui est rentré, ce qui manque
              </p>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="space-y-5 pt-6">

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Mois :</span>
              <Select value={moisBilan || undefined} onValueChange={setMoisBilan}>
                <SelectTrigger className="w-52 border-gray-300 bg-white">
                  <SelectValue placeholder="Choisir un mois" />
                </SelectTrigger>
                <SelectContent>
                  {moisScolaires.map((m) => (
                    <SelectItem key={m.reference} value={m.reference}>{m.nom}</SelectItem>
                  ))}
                  {horsCalendrier.map((m) => (
                    <SelectItem key={m.reference} value={m.reference}>{m.nom} · hors calendrier</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="ml-auto flex items-center gap-2">
                <ArrowUpDown size={14} className="text-gray-400" />
                <Select value={tri} onValueChange={setTri}>
                  <SelectTrigger className="h-8 w-48 border-gray-300 bg-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reste">Reste dû, décroissant</SelectItem>
                    <SelectItem value="taux">Taux le plus faible</SelectItem>
                    <SelectItem value="classe">Section et classe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {chargement ? (
              <div className="flex h-40 items-center justify-center">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#246990] border-t-transparent" />
                  <span className="font-medium text-[#246990]">Chargement…</span>
                </div>
              </div>
            ) : periode.hors_calendrier ? (
              <div className="flex items-start gap-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
                <CalendarOff size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <div>
                  <h5 className="font-semibold text-amber-900">Mois hors calendrier scolaire</h5>
                  <p className="mt-1 text-sm text-amber-800">
                    Aucune échéance de scolarité n'est rattachée à ce mois : il n'y a rien à recouvrer.
                  </p>
                </div>
              </div>
            ) : !totaux || classes.length === 0 ? (
              <EtatVide
                icone={FileText}
                titre="Aucune échéance sur ce mois"
                description="Aucune classe ne porte d'échéance de scolarité pour le mois choisi."
              />
            ) : (
              <>
                {/* La synthèse, avant le détail. */}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {[
                    { libelle: `Attendu en ${nomMois}`, valeur: totaux.attendu, ton: 'text-[#246990]' },
                    { libelle: 'Encaissé à ce jour', valeur: totaux.encaisse, ton: 'text-emerald-700' },
                    { libelle: 'Reste dû', valeur: totaux.reste, ton: 'text-red-700' },
                    { libelle: 'Taux de recouvrement', valeur: `${totaux.taux} %`, ton: 'text-[#246990]', brut: true }
                  ].map((carte) => (
                    <div key={carte.libelle} className="rounded-lg border border-gray-200 bg-white p-3">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">{carte.libelle}</p>
                      <p className={`mt-1 text-lg font-bold tabular-nums ${carte.ton}`}>
                        {carte.brut ? carte.valeur : formatMontant(carte.valeur)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* L'état n'est jamais définitif : un retardataire viendra s'y
                    ajouter. Le dire, sinon deux captures prises à un mois
                    d'écart passeraient pour une erreur. */}
                <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <Info size={13} />
                  État au {new Date().toLocaleDateString('fr-FR')} — les règlements en retard viennent s'y ajouter.
                  {totaux.eleves} élève(s) concerné(s), dont {totaux.eleves_soldes} soldé(s)
                  et {totaux.eleves_rien_verse} sans aucun versement.
                </p>

                {/* La règle du tableau, écrite. Elle se déduisait des
                    intitulés ; se déduire n'est pas se lire. */}
                <div className="flex items-start gap-2 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3">
                  <Info size={15} className="mt-0.5 flex-shrink-0 text-[#246990]" />
                  <p className="text-[11px] leading-relaxed text-gray-700">
                    Chaque ligne ne porte que les <strong>frais de {nomMois}</strong>. Un règlement fait
                    en mars pour {nomMois} compte ici ; un règlement fait en {nomMois} pour un autre mois
                    n'y figure pas — il est dans le tableau de son propre mois, et se retrouve plus bas
                    dans « Encaissé pendant {nomMois} ».
                  </p>
                </div>

                {/* Le tableau. Neuf colonnes ne tiennent pas sous 1024 px :
                    la charte demande qu'un contenu large défile chez lui. */}
                <div className="overflow-x-auto">
                  <div className={`${GRILLE} min-w-[1180px] border-b-2 border-gray-300 bg-gray-50`}>
                    <span className="px-2.5 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-600">Section · Classe</span>
                    <span className="px-2.5 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-600">Élv</span>
                    {COLONNES(nomMois).map((c, index) => (
                      <span
                        key={c.cle}
                        title={c.aide}
                        className={`flex cursor-help flex-col items-end px-2.5 py-2 ${GROUPES.has(index) ? SEPARATEUR : ''}`}
                      >
                        <span className="text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-700">
                          {c.libelle}
                        </span>
                        <span className="mt-0.5 text-right text-[9px] font-normal normal-case leading-tight text-gray-500">
                          {c.precision}
                        </span>
                      </span>
                    ))}
                    <span className={`flex flex-col items-end px-2.5 py-2 ${SEPARATEUR}`}>
                      <span className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-700">Taux</span>
                      <span className="mt-0.5 text-[9px] font-normal normal-case leading-tight text-gray-500">de {nomMois}</span>
                    </span>
                  </div>

                  <div className="min-w-[1180px] divide-y divide-gray-100">
                    {classes.map((c) => (
                      <div
                        key={c.classe_id || c.classe}
                        className={`${GRILLE} transition-colors even:bg-gray-50/60 hover:bg-[#eef7fb]`}
                      >
                        <div className="min-w-0 px-2.5 py-2">
                          <p className="truncate text-xs font-semibold text-gray-900">{c.classe}</p>
                          <p className="truncate text-[10px] text-gray-500">{c.section}</p>
                        </div>
                        <span className="px-2.5 py-2 text-right text-xs tabular-nums text-gray-600">{c.eleves}</span>
                        <Cellule valeur={c.attendu} ton="text-[#246990]" groupe />
                        <Cellule valeur={c.encaisse} ton="text-emerald-700" gras />
                        <Cellule valeur={c.pendant} ton="text-gray-500" groupe />
                        <CelluleRattrapage valeur={c.rattrapage} mois={c.mois_reglement} />
                        <Cellule valeur={c.reste} ton={c.reste > 0 ? 'text-red-700' : 'text-gray-400'} gras groupe />
                        <Taux valeur={c.taux} />
                      </div>
                    ))}

                    {/* Le total école, sur le même gabarit — donc aligné. */}
                    <div className={`${GRILLE} border-t-2 border-gray-300 bg-gray-100`}>
                      <span className="px-2.5 py-2.5 text-xs font-bold text-gray-900">Total école</span>
                      <span className="px-2.5 py-2.5 text-right text-xs font-bold tabular-nums text-gray-700">{totaux.eleves}</span>
                      <Cellule valeur={totaux.attendu} ton="text-[#246990]" gras groupe />
                      <Cellule valeur={totaux.encaisse} ton="text-emerald-700" gras />
                      <Cellule valeur={totaux.pendant} ton="text-gray-600" gras groupe />
                      <Cellule valeur={totaux.rattrapage} ton="text-amber-700" gras />
                      <Cellule valeur={totaux.reste} ton="text-red-700" gras groupe />
                      <Taux valeur={totaux.taux} />
                    </div>
                  </div>
                </div>

                {/* `montant_paye` fait foi, les transactions ventilent. Les deux
                    ne se recouvrent pas exactement sur les lignes anciennes :
                    l'écart est montré plutôt que dilué. */}
                {Math.abs(totaux.ecart_tracage || 0) > 0 && (
                  <p className="text-[11px] text-gray-500">
                    Écart de traçage : <strong className="tabular-nums">{formatMontant(Math.abs(totaux.ecart_tracage))}</strong>{' '}
                    d'encaissé {totaux.ecart_tracage > 0 ? 'sans' : 'en excès par rapport aux'} transaction(s) datée(s).
                    « Dans le mois » et « en rattrapage » ne couvrent donc pas exactement l'encaissé.
                  </p>
                )}

                {/* ── Volet 2 : ce qui a été ENREGISTRÉ pendant le mois ── */}
                <div className="rounded-lg border-2 border-[#319190]/25 bg-[#319190]/5">
                  <div className="border-b border-[#319190]/25 bg-[#319190]/10 p-4">
                    <h3 className="text-base font-semibold text-[#246990]">
                      Encaissé pendant {nomMois}
                    </h3>
                    <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
                      L'autre axe : l'argent entré en caisse pendant {nomMois}, quel que soit le mois
                      qu'il solde. C'est ici — et pas dans le tableau — que figurent les frais d'autres mois.
                    </p>
                  </div>

                  <div className="space-y-3 p-4">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-sm font-semibold text-gray-800">Total enregistré</span>
                      <strong className="text-lg tabular-nums text-gray-900">
                        {formatMontant(enregistrements.total || 0)}
                      </strong>
                    </div>

                    <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <ArrowDownRight size={16} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-4">
                          <span className="text-sm font-medium text-emerald-900">Frais de {nomMois}</span>
                          <strong className="tabular-nums text-emerald-700">
                            {formatMontant(enregistrements.pour_le_mois || 0)}
                          </strong>
                        </div>
                        <p className="mt-0.5 text-[11px] text-emerald-800">
                          Entre dans le cumul de {nomMois}.
                        </p>
                      </div>
                    </div>

                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="flex items-start gap-2">
                        <ArrowRightLeft size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between gap-4">
                            <span className="text-sm font-medium text-amber-900">Frais d'autres mois</span>
                            <strong className="tabular-nums text-amber-700">
                              {formatMontant(enregistrements.rattrapage || 0)}
                            </strong>
                          </div>
                          <p className="mt-0.5 text-[11px] text-amber-800">
                            N'entre pas dans le cumul de {nomMois} : ces règlements soldent d'autres échéances.
                          </p>
                        </div>
                      </div>

                      {/* Chaque mois bascule vers son propre tableau : c'est là
                          que le même règlement apparaît comme recouvrement. */}
                      {(enregistrements.detail || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {enregistrements.detail.map((d) => (
                            <button
                              key={d.mois_id}
                              type="button"
                              onClick={() => setMoisBilan(d.reference)}
                              title={`Voir le tableau de ${d.nom} — ce règlement y figure comme recouvrement`}
                              className="inline-flex items-baseline gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs transition-colors hover:bg-amber-100"
                            >
                              <span className="font-medium text-gray-800">{d.nom}</span>
                              <strong className="tabular-nums text-amber-700">{formatMontant(d.total)}</strong>
                              <span className="text-[10px] text-gray-500">{d.lignes} lignes</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {Number(enregistrements.autres_encaissements || 0) > 0 && (
                      <div className="flex items-baseline justify-between gap-4 border-t pt-3">
                        <span className="text-xs text-gray-600">
                          Autres encaissements — inscriptions, examens, frais annexes
                        </span>
                        <strong className="text-xs tabular-nums text-gray-700">
                          {formatMontant(enregistrements.autres_encaissements)}
                        </strong>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default EtatEcoleSection;
